import { SharedAngular } from '@Client/@types/sharedAngular';
import {
  FlowsImInBoxFilters,
  FlowsImInTabId
} from '@Client/runner.flows.imin/FlowsImIn';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import {
  IActorDeletedMessage,
  IFlowModelNameChangedMessage,
  IRunnerNewFlowCommentCountMessage,
  IWithdrawFlowMessage
} from '@Shared.Angular/@types/pubSub';
import angular from 'angular';
import { IGroupedFlowsForDisplay } from './flows.formatter';

export default class FlowImInService {
  public static $inject = [
    'flowApiService',
    '$q',
    'pubsubService',
    'momentService',
    'APP_CONFIG',
    'runnerFlowsFormatter'
  ];

  private categoryId: Guid = EmptyGuid;

  private tabId: FlowsImInTabId = FlowsImInTabId.STARTED_BY_ME;
  private fromDate: Date;
  private toDate: Date;

  private tagIds: Guid[] = [];
  private flowModelId: Guid = EmptyGuid;

  private boxFilterId: FlowsImInBoxFilters;

  private groupedFlows: IGroupedFlowsForDisplay[] = [];
  private groupedFlowsPromise: angular.IPromise<IGroupedFlowsForDisplay[]>;

  constructor(
    private flowApiService: FlowApiService,
    private $q: angular.IQService,
    private pubsubService: SharedAngular.PubSubService,
    private moment: Moment,
    private APP_CONFIG: SharedAngular.APP_CONFIG,
    private runnerFlowsFormatter: RunnerFlowsFormatterService
  ) {
    this.groupedFlowsPromise = this.$q.resolve(this.groupedFlows);
    this.fromDate = moment()
      .subtract(APP_CONFIG.flowsInDateFilterDefaultRange, 'months')
      .toDate();
    this.toDate = moment().toDate();
    this.boxFilterId = this.APP_CONFIG.showDashboardsV1
      ? FlowsImInBoxFilters.ALL
      : FlowsImInBoxFilters.IN_PROGRESS;

    const subscriberId = 'flowImInService';
    this.pubsubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      (event: string, eventData: string) => {
        const { id, name } = JSON.parse(
          eventData
        ) as IFlowModelNameChangedMessage;
        this.groupedFlows.forEach((group) => {
          if (group.flowModelId === id) {
            group.groupName = name;
          }
        });
      },
      subscriberId
    );

    this.pubsubService.subscribeMany(
      [
        'SIGNALR_RUNNER_START_FLOW',
        'SIGNALR_RUNNER_COMPLETE_STEP',
        'SIGNALR_RUNNER_REASSIGN_STEP',
        'SIGNALR_RUNNER_USER_TEAM_UPDATED',
        'SIGNALR_RUNNER_STEP_TASK_CANCELLED',
        'SIGNALR_RUNNER_STEP_TASK_CREATED',
        'SIGNALR_RUNNER_STEP_TASK_UPDATED',
        'SIGNALR_RUNNER_STEP_INTEGRATION_PROCESSING_UPDATED'
      ],
      () => this.getRefreshedFlows(this.tabId, this.fromDate, this.toDate),
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_RUNNER_WITHDRAW_FLOW',
      (event, eventData: string) => {
        const { flowId } = JSON.parse(eventData) as IWithdrawFlowMessage;
        this.groupedFlows.forEach((group) => {
          group.flows.forEach((flow) => {
            if (flow.flowId !== flowId) {
              return;
            }
            flow.finalisedReason = 'Withdrawn';
            flow.finalisedDate = new Date().toISOString();
          });
        });
      },
      subscriberId
    );

    this.pubsubService.subscribe(
      'SIGNALR_ACTOR_DELETED',
      (event, eventData: string) => {
        const { actorName, replacementActorName } = JSON.parse(
          eventData
        ) as IActorDeletedMessage;
        this.groupedFlows.forEach((group) => {
          group.flows.forEach((flow) => {
            if (!flow.waitingForName.includes(actorName)) {
              return;
            }
            const names = flow.waitingForName.split(',').map((name) => {
              return name.trim() === actorName
                ? replacementActorName
                : name.trim();
            });
            flow.waitingForName = names.join(', ');
          });
        });
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'WORKFLOW_DELETED',
      (event, flowId: Guid) => {
        this.groupedFlows.forEach((group) => {
          group.flows = group.flows.filter((flow) => flow.flowId !== flowId);
        });
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_RUNNER_NEW_FLOW_COMMENT_COUNT',
      (event, eventData: string) => {
        const { flowId, commentCount } = JSON.parse(
          eventData
        ) as IRunnerNewFlowCommentCountMessage;
        this.groupedFlows.forEach((group) => {
          group.flows.forEach((flow) => {
            if (flow.flowId !== flowId) {
              return;
            }
            flow.commentCount = commentCount;
          });
        });
      },
      subscriberId
    );
  }

  public getSavedFilters() {
    return {
      categoryId: this.categoryId,
      tab: this.tabId,
      fromDate: this.fromDate,
      toDate: this.toDate,
      tagIds: this.tagIds,
      flowModelId: this.flowModelId,
      boxFilterId: this.boxFilterId
    };
  }

  public saveFilters(
    categoryId: Guid,
    tab: FlowsImInTabId,
    fromDate: Date,
    toDate: Date,
    tagIds: Guid[],
    flowModelId: Guid,
    boxFilterId: FlowsImInBoxFilters
  ) {
    this.categoryId = categoryId;
    this.tabId = tab;
    this.fromDate = fromDate;
    this.toDate = toDate;
    this.tagIds = tagIds;
    this.flowModelId = flowModelId;
    this.boxFilterId = boxFilterId;
  }

  /** Get Flows ImIn from the API */
  public getRefreshedFlows(
    newTab: FlowsImInTabId,
    newFromDate: Date,
    newToDate: Date
  ) {
    this.tabId = newTab;
    this.fromDate = newFromDate;
    this.toDate = newToDate;

    const onlyStartedByMe = this.tabId === FlowsImInTabId.STARTED_BY_ME;
    const startDateString = this.moment(this.fromDate)
      .startOf('day')
      .toISOString();
    const endDateString = this.moment(this.toDate).endOf('day').toISOString();
    this.groupedFlowsPromise = this.flowApiService
      .getFlowsImIn(onlyStartedByMe, startDateString, endDateString)
      .then((response) => {
        this.groupedFlows =
          this.runnerFlowsFormatter.setGroupedFlowDisplayProperties(
            response || []
          );
        return this.groupedFlows;
      });
    return this.groupedFlowsPromise;
  }

  /** Return cached Flows if any and appropriate, otherwise get from the API */
  public getFlows(
    targetTabId: FlowsImInTabId,
    targetFromDate: Date,
    targetToDate: Date
  ) {
    if (
      targetTabId !== this.tabId ||
      targetFromDate !== this.fromDate ||
      targetToDate !== this.toDate
    ) {
      return this.getRefreshedFlows(targetTabId, targetFromDate, targetToDate);
    } else {
      return this.groupedFlowsPromise.then(() => {
        if (this.groupedFlows.length > 0) {
          return this.groupedFlows;
        }
        return this.getRefreshedFlows(
          targetTabId,
          targetFromDate,
          targetToDate
        );
      });
    }
  }

  public getFlowCount() {
    return this.groupedFlows.reduce(
      (count, group) => count + group.flows.length,
      0
    );
  }
}

angular
  .module('flowingly.runner.services')
  .service('flowImInService', FlowImInService);

export type FlowImInServiceType = InstanceType<typeof FlowImInService>;
